/**
 * DayList (Ted #20 Part A → #940; the list every #919 number opens) — every
 * service scheduled on one day, one row per booking, filterable.
 *
 * Ted, 09-09: *"The Facility Calendar gives us a snapshot of what is
 * specifically scheduled/arriving on a particular day … full-day daycare
 * dogs, half-day daycare dogs, Boutique Hotel dogs beginning their stay
 * that night, assessments, grooming appointments, hikes … The Dashboard
 * tells us our numbers and physical dog count, whereas the Facility Calendar
 * tells us specifically which dogs and services make up that day."* And
 * the Riggins example: *"If a dog has both a facility service and an
 * appointment that day, I want both visible wherever that dog appears:
 * Riggins — Half-Day Daycare | Bath/Grooming."*
 *
 * ONE component (the #919 note: "do not add a fifth surface"): the
 * `/calendar/day` page renders it in full; a tapped rollup number renders
 * it pre-filtered. Rows come from the same `/rollup` read as the numbers,
 * so the list can never disagree with the table.
 *
 * Design-Philosophy principles served:
 *  - #1 One glance — dog first, then service (with the dog's other services
 *    on the same line), then the status word, the time, the owner.
 *  - #2 Next action obvious — filters are chips, a dog opens its profile.
 *  - #5 Never lie — status is a WORD with a tone, never a color alone; an
 *    empty list says why; a structural zero says so.
 *  - #7 Built for hands — chips and rows are ≥44 px; at 375 px rows stack.
 */
import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
// Ted #28 (#1050) — the facility calendar is one of the six surfaces the
// flag must follow the dog to; the one renderer, never a local chip.
import { DogFlagChips } from '../dog-first/index.js';
import { OwnerLink } from '../owner-link/index.js';
import type { DayRollupItem, DayRollupServiceRow } from './dashboard-api.js';
import {
  DAY_VIEWS,
  LIST_STATUS_LABEL,
  LIST_STATUS_TONE,
  countDogs,
  filterDayItems,
  otherServicesByDog,
  timeWordsOf,
  type DayListFilter,
  type DayView,
} from './day-list-filters.js';
import { serviceColorClass } from '@ferro/core/modules/tenant-settings/service-colors.js';
import '../../service-colors.css';
import './day-list.css';
import { DayCareLine } from './DayCareLine.js';
import { AteNoneBadge } from '../care-log/AteNoneBadge.js';

interface DayListProps {
  readonly items: ReadonlyArray<DayRollupItem>;
  readonly services: ReadonlyArray<DayRollupServiceRow>;
  readonly filter: DayListFilter;
  readonly onFilterChange: (next: DayListFilter) => void;
  /** #5 — server says today's zeros may be structural. */
  readonly dataSuspect: boolean;
}

function Chip({
  active,
  onClick,
  children,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testid: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`ferro-daylist__chip${active ? ' ferro-daylist__chip--active' : ''}`}
      aria-pressed={active}
      onClick={onClick}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

export function DayList({
  items,
  services,
  filter,
  onFilterChange,
  dataSuspect,
}: DayListProps): JSX.Element {
  // Ted: "an arrow on the left side collapses a lot of the filtering
  // information and gives you a much cleaner view." Collapsed by default on
  // the phone, open on the desk (CSS decides; this state is the toggle).
  const [railOpen, setRailOpen] = useState(false);

  const visible = useMemo(() => filterDayItems(items, filter), [items, filter]);
  // Ted #23 (#984) / Ted #20 Part B (#940): *"A leg that exists but has no
  // assignee shows in a 'needs a specialist' lane, never nowhere."* The lane
  // is a SLICE of the same list, not a second list — every row below appears
  // exactly once, here or in the main list, so the summary count stays true
  // and the desk can never read one dog as two.
  const needsSpecialist = useMemo(
    () => visible.filter((i) => i.needsSpecialist === true),
    [visible],
  );
  const scheduled = useMemo(
    () => visible.filter((i) => i.needsSpecialist !== true),
    [visible],
  );
  const siblings = useMemo(() => otherServicesByDog(items), [items]);
  const dogTotal = countDogs(visible);
  const activeFilters =
    (filter.serviceTypeId ? 1 : 0) + (filter.view !== 'all' ? 1 : 0);

  const setView = (view: DayView): void => onFilterChange({ ...filter, view });
  const setService = (serviceTypeId: string | null): void =>
    onFilterChange({ ...filter, serviceTypeId });

  return (
    <section className="ferro-daylist" data-testid="day-list" aria-label="Day list">
      <div className="ferro-daylist__bar">
        <p className="ferro-daylist__summary" data-testid="day-list-summary">
          <strong>{dogTotal}</strong> {dogTotal === 1 ? 'dog' : 'dogs'} ·{' '}
          {visible.length} {visible.length === 1 ? 'booking' : 'bookings'}
        </p>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-daylist__rail-toggle"
          aria-expanded={railOpen}
          aria-controls="day-list-filters"
          onClick={() => setRailOpen((o) => !o)}
          data-testid="day-list-filters-toggle"
        >
          {railOpen ? 'Hide filters' : 'Filters'}
          {activeFilters > 0 ? ` (${activeFilters})` : ''}
        </button>
      </div>

      <div
        id="day-list-filters"
        className={`ferro-daylist__rail${railOpen ? ' ferro-daylist__rail--open' : ''}`}
        data-testid="day-list-filters"
      >
        <div className="ferro-daylist__group" role="group" aria-label="Service">
          <span className="ferro-daylist__group-label">Service</span>
          <Chip
            active={filter.serviceTypeId === null}
            onClick={() => setService(null)}
            testid="day-list-service-all"
          >
            All services
          </Chip>
          {services.map((s) => (
            <Chip
              key={s.serviceTypeId}
              active={filter.serviceTypeId === s.serviceTypeId}
              onClick={() => setService(s.serviceTypeId)}
              testid={`day-list-service-${s.name}`}
            >
              {s.displayName}
            </Chip>
          ))}
        </div>
        <div className="ferro-daylist__group" role="group" aria-label="Status">
          <span className="ferro-daylist__group-label">Show</span>
          {DAY_VIEWS.map((v) => (
            <Chip
              key={v.key}
              active={filter.view === v.key}
              onClick={() => setView(v.key)}
              testid={`day-list-view-${v.key}`}
            >
              {v.label}
            </Chip>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="ferro-daylist__empty" data-testid="day-list-empty">
          {dataSuspect
            ? 'Schedule may be incomplete — see the notice above.'
            : activeFilters > 0
              ? 'No dogs match these filters.'
              : 'Nothing scheduled for this day.'}
        </p>
      ) : (
        <>
        {needsSpecialist.length > 0 ? (
          <section
            className="ferro-daylist__lane"
            data-testid="day-list-needs-specialist"
            aria-label="Needs a specialist"
          >
            <h3 className="ferro-daylist__lane-title">
              Needs a specialist
              <span className="ferro-daylist__lane-count">{needsSpecialist.length}</span>
            </h3>
            <p className="ferro-daylist__lane-note">
              Added services with nobody assigned yet. Assign someone so they
              land on that person&rsquo;s schedule.
            </p>
            <ul className="ferro-daylist__rows">
              {needsSpecialist.map((it) => (
                <DayRow key={it.bookingId} it={it} />
              ))}
            </ul>
          </section>
        ) : null}
        <ul className="ferro-daylist__rows">
          {scheduled.map((it) => {
            const time = timeWordsOf(it);
            // Every other service each of this row's dogs has today, minus
            // this booking — "Riggins — Half-Day Daycare | Bath/Grooming".
            const others = [
              ...new Set(
                it.dogs.flatMap((d) =>
                  (siblings.get(d.id) ?? [])
                    .filter((s) => s.bookingId !== it.bookingId)
                    .map((s) => s.serviceDisplayName),
                ),
              ),
            ];
            return (
              <li
                key={it.bookingId}
                className={['ferro-daylist__row', `ferro-daylist__row--${it.listStatus}`, serviceColorClass(it.color)]
                  .filter(Boolean)
                  .join(' ')}
                data-testid={`day-list-row-${it.bookingId}`}
                data-status={it.listStatus}
                data-color={it.color ?? ''}
              >
                <div className="ferro-daylist__who">
                  <span className="ferro-daylist__dogs">
                    {it.dogs.map((d, i) => (
                      <span key={d.id}>
                        {i > 0 ? ', ' : ''}
                        <Link className="ferro-daylist__dog" to={`/dogs/${encodeURIComponent(d.id)}`}>
                          {d.name}
                        </Link>
                        {/* Ted #28 (#1050) — the stay's flags beside the name. */}
                        {/* Sawyer #1295 — "Ate none at breakfast" from the first ate-none meal. */}
                        <AteNoneBadge ateNone={d.ateNone} dogName={d.name} testId={`day-list-ate-none-${it.bookingId}-${d.id}`} />
                        <DogFlagChips flags={d.flags} testIdPrefix={`day-list-${it.bookingId}-`} />
                        {/* Sawyer 08 meds-feeding (A3) — feeding + med times. */}
                        <DayCareLine care={d.care} service={it.serviceType} testId={`day-list-care-${it.bookingId}-${d.id}`} />
                      </span>
                    ))}
                  </span>
                  <span className="ferro-daylist__service">
                    {/* #984 — a leg reads as what it IS ("Nail Trim"), not as
                        the service type it rides on ("Grooming"). */}
                    {it.addOnName ?? it.serviceDisplayName}
                    {it.isLeg ? (
                      <span
                        className="ferro-daylist__added"
                        data-testid={`day-list-added-${it.bookingId}`}
                      >
                        {' '}· added service
                      </span>
                    ) : null}
                    {others.length > 0 ? (
                      <span className="ferro-daylist__siblings" data-testid={`day-list-siblings-${it.bookingId}`}>
                        {' '}| {others.join(' | ')}
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className="ferro-daylist__when">
                  <span
                    className={`ferro-status-pill ferro-status-pill--${LIST_STATUS_TONE[it.listStatus]}`}
                    data-testid={`day-list-status-${it.bookingId}`}
                  >
                    {LIST_STATUS_LABEL[it.listStatus]}
                  </span>
                  {it.staysTonight ? (
                    <span className="ferro-daylist__tag" data-testid={`day-list-overnight-${it.bookingId}`}>
                      Overnight tonight
                    </span>
                  ) : null}
                  {time ? <span className="ferro-daylist__time">{time}</span> : null}
                </div>
                <div className="ferro-daylist__owner">
                  {/* Sawyer 01 owner-links (F2) — role-gated: floor roles see text, never a
                      link that ends at "Access restricted". */}
                  <OwnerLink name={it.customerName} customerId={it.customerId} />
                  {/* Ted #28 Phase 2 — the owner's flags under the owner. */}
                  <DogFlagChips flags={it.dogs[0]?.flags} scope="account" testIdPrefix={`day-list-owner-${it.bookingId}-`} />
                </div>
              </li>
            );
          })}
        </ul>
        </>
      )}
    </section>
  );
}

/**
 * One day-list row, used by the "needs a specialist" lane. The main list
 * keeps its inline renderer (it also prints the dog's OTHER services, which
 * the lane deliberately does not — the lane answers one question: who is
 * doing this?).
 */
function DayRow({ it }: { it: DayRollupItem }): JSX.Element {
  const time = timeWordsOf(it);
  return (
    <li
      className={['ferro-daylist__row', `ferro-daylist__row--${it.listStatus}`, serviceColorClass(it.color)]
        .filter(Boolean)
        .join(' ')}
      data-testid={`day-list-row-${it.bookingId}`}
      data-status={it.listStatus}
      data-leg="true"
    >
      <div className="ferro-daylist__who">
        <span className="ferro-daylist__dogs">
          {it.dogs.map((d, i) => (
            <span key={d.id}>
              {i > 0 ? ', ' : ''}
              <Link className="ferro-daylist__dog" to={`/dogs/${encodeURIComponent(d.id)}`}>
                {d.name}
              </Link>
            </span>
          ))}
        </span>
        <span className="ferro-daylist__service">
          {it.addOnName ?? it.serviceDisplayName}
          <span className="ferro-daylist__added" data-testid={`day-list-added-${it.bookingId}`}>
            {' '}· added service
          </span>
        </span>
      </div>
      <div className="ferro-daylist__when">
        <span
          className={`ferro-status-pill ferro-status-pill--${LIST_STATUS_TONE[it.listStatus]}`}
          data-testid={`day-list-status-${it.bookingId}`}
        >
          {LIST_STATUS_LABEL[it.listStatus]}
        </span>
        {time ? <span className="ferro-daylist__time">{time}</span> : <span className="ferro-daylist__time">Time not set</span>}
      </div>
      <div className="ferro-daylist__owner">
        <OwnerLink name={it.customerName} customerId={it.customerId} />
        <DogFlagChips flags={it.dogs[0]?.flags} scope="account" testIdPrefix={`day-list-owner-${it.bookingId}-`} />
      </div>
    </li>
  );
}
